/**
 * <product-bundle-form> Custom Element
 *
 * Product structure: each bundle product = 1 color, only has "Size" option.
 * Color is identified by product ID (name extracted from title).
 *
 * Handles:
 * - Size selection (shared across all slots)
 * - Color/product selection per slot (selecting a color = selecting a product)
 * - Inventory / OOS checking → disables unavailable colors per size
 * - Add-to-cart: adds N items via /cart/add.js with shared _bundle_group property
 */
if (!customElements.get('product-bundle-form')) {
  class ProductBundleForm extends HTMLElement {
    constructor() {
      super();
      this.bundleProducts = [];
      this.selectedSize = null;
      /** @type {Map<number, number>} slotIndex → selected product ID */
      this.selectedProductIds = new Map();
    }

    connectedCallback() {
      const jsonEl = this.querySelector('[data-bundle-products-json]');
      if (!jsonEl) return;

      try {
        this.bundleProducts = JSON.parse(jsonEl.textContent);
      } catch (e) {
        console.error('[BundleForm] Failed to parse products JSON', e);
        return;
      }

      if (!this.bundleProducts.length) return;

      this.bundleCount = parseInt(this.dataset.bundleCount, 10) || this.bundleProducts.length;

      // Cache DOM
      this.sizeInputs = this.querySelectorAll('input[name="bundle-size"]');
      this.itemEls = this.querySelectorAll('[data-bundle-item-index]');
      this.addButton = this.querySelector('[data-bundle-add-to-cart]');

      this.errorWrapper = this.querySelector('.bundle-form__error-message-wrapper');
      this.errorMessage = this.querySelector('.bundle-form__error-message');

      // Initial state
      const checkedSize = this.querySelector('input[name="bundle-size"]:checked');
      if (checkedSize) this.selectedSize = checkedSize.value;

      // Read initial color (product) selections
      this.itemEls.forEach((itemEl) => {
        const idx = parseInt(itemEl.dataset.bundleItemIndex, 10);
        const checked = itemEl.querySelector(`input[name="bundle-color-${idx}"]:checked`);
        if (checked) this.selectedProductIds.set(idx, parseInt(checked.value, 10));
      });

      // Bind size change
      this.sizeInputs.forEach((input) => {
        input.addEventListener('change', this._onSizeChange.bind(this));
      });

      // Bind color change per slot
      this.itemEls.forEach((itemEl) => {
        itemEl.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.addEventListener('change', this._onColorChange.bind(this));
        });
      });

      this.addButton.addEventListener('click', this._onAddToCart.bind(this));

      // Initial run
      this._validateAvailability();
    }

    /* ─── Event Handlers ─── */

    _onSizeChange(e) {
      this.selectedSize = e.target.value;
      this._validateAvailability();
      this._clearError();
    }

    _onColorChange(e) {
      const itemEl = e.target.closest('[data-bundle-item-index]');
      if (!itemEl) return;
      const idx = parseInt(itemEl.dataset.bundleItemIndex, 10);
      this.selectedProductIds.set(idx, parseInt(e.target.value, 10));

      // Update slot image
      const img = itemEl.querySelector('[data-bundle-slot-image]');
      if (img && e.target.dataset.productImage) {
        img.src = e.target.dataset.productImage;
      }


      this._clearError();
    }

    /* ─── Inventory Validation ─── */

    _validateAvailability() {
      if (!this.selectedSize) return;

      this.itemEls.forEach((itemEl) => {
        const idx = parseInt(itemEl.dataset.bundleItemIndex, 10);
        const colorInputs = itemEl.querySelectorAll(`input[name="bundle-color-${idx}"]`);
        let hasAvailable = false;

        colorInputs.forEach((input) => {
          const productId = parseInt(input.value, 10);
          const productData = this.bundleProducts.find((p) => p.id === productId);
          if (!productData) return;

          // Find variant matching the selected size (option1 = size)
          const variant = productData.variants.find((v) => v.option1 === this.selectedSize);
          const isAvailable = variant && variant.available;

          if (isAvailable) {
            input.classList.remove('bundle-oos');
            hasAvailable = true;
          } else {
            input.classList.add('bundle-oos');
            if (input.checked) {
              input.checked = false;
              this.selectedProductIds.delete(idx);
            }
          }
        });

        // Auto-select first available if nothing selected
        if (!itemEl.querySelector(`input[name="bundle-color-${idx}"]:checked`) && hasAvailable) {
          const firstAvail = Array.from(colorInputs).find((i) => !i.classList.contains('bundle-oos'));
          if (firstAvail) {
            firstAvail.checked = true;
            this.selectedProductIds.set(idx, parseInt(firstAvail.value, 10));
            // Update image
            const img = itemEl.querySelector('[data-bundle-slot-image]');
            if (img && firstAvail.dataset.productImage) {
              img.src = firstAvail.dataset.productImage;
            }
          }
        }
      });


    }

    /**
     * Find a variant by size for a given product.
     * Each product only has Size as option1.
     */
    _findVariant(productData, size) {
      return productData.variants.find((v) => v.option1 === size);
    }



    /* ─── Add To Cart ─── */

    async _onAddToCart() {
      if (!this.selectedSize) {
        this._showError('Please select a size');
        return;
      }

      if (this.submitting) return;
      this.submitting = true;

      const bundleGroupId = `bundle_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      const rawParentVariantId = this.dataset.parentVariantId;
      // Cart Transform linesMerge requires full GID format
      const parentVariantId = rawParentVariantId.startsWith('gid://')
        ? rawParentVariantId
        : `gid://shopify/ProductVariant/${rawParentVariantId}`;
      // Bundle target price for Cart Transform price adjustment
      const bundlePrice = this.dataset.parentPrice || '';
      const items = [];

      for (let idx = 0; idx < this.bundleCount; idx++) {
        const productId = this.selectedProductIds.get(idx);
        if (!productId) {
          this._showError(`Please select a color for item ${idx + 1}`);
          this.submitting = false;
          return;
        }

        const productData = this.bundleProducts.find((p) => p.id === productId);
        if (!productData) {
          this._showError(`Product not found for item ${idx + 1}`);
          this.submitting = false;
          return;
        }

        const variant = this._findVariant(productData, this.selectedSize);
        if (!variant || !variant.available) {
          this._showError(`"${productData.color}" in size ${this.selectedSize} is out of stock`);
          this.submitting = false;
          return;
        }

        items.push({
          id: variant.id,
          quantity: 1,
          properties: {
            _bundle_group: bundleGroupId,
            _bundle_parent_id: this.dataset.parentProductId,
            _bundle_parent_variant_id: parentVariantId,
            _bundle_price: bundlePrice,
            _bundle_item_color: productData.color,
            _bundle_item_size: this.selectedSize,
          },
        });
      }

      // Disable button
      this.addButton.setAttribute('disabled', 'true');
      this.addButton.textContent = 'ADDING...';

      const config = window.fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      config.body = JSON.stringify({
        items,
        sections: this.getSectionsToRender().map((s) => s.section),
        sections_url: window.location.pathname,
      });

      try {
        // serializeCartAdd queues rapid adds so they don't fire concurrently
        // (the pattern Shopify throttles hardest).
        const doAdd = () => fetch(`${window.routes.cart_add_url}`, config);
        const response = await (window.serializeCartAdd ? window.serializeCartAdd(doAdd) : doAdd());

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
        }

        const json = await response.json();

        if (json.status) {
          this._showError(json.description || 'Failed to add bundle to cart');
        } else {
          try {
            this.renderLiveSections(json);
          } catch (e) {
            console.error('[BundleForm] Failed to render sections after add', e);
          }
          // Secondary cart sync (gift/donation/count refresh) is debounced so
          // rapid multi-add does not stampede the cart API. See global.js.
          if (typeof window.runPostAddChores === 'function') window.runPostAddChores();
        }
      } catch (error) {
        this._showError(`Failed to add bundle: ${error.message}`);
      }

      // Button state: ADDED → reset
      this.addButton.textContent = 'ADDED';
      this.addButton.classList.add('altLoader');
      setTimeout(() => {
        this.addButton.removeAttribute('disabled');
        this.addButton.classList.remove('altLoader');
        this.addButton.textContent = 'ADD BUNDLE TO CART';
      }, 1000);

      // AutoGift / AutoDonation now run via the debounced window.runPostAddChores()
      // scheduled in the success branch above — see global.js.

      this.submitting = false;
    }

    getSectionsToRender() {
      const sections = [];
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        sections.push(
          { section: 'cart-drawer' },
          { section: 'cart-icon-bubble' }
        );
      }

      const cartNotification = document.querySelector('cart-notification');
      if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
        sections.push(...cartNotification.getSectionsToRender());
      }

      return sections;
    }

    renderLiveSections(json) {
      const cartDrawer = document.querySelector('cart-drawer');

      if (cartDrawer) {
        cartDrawer.renderContents(json);
      } else {
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification) {
          setTimeout(() => {
            cartNotification.renderContents(json);
          }, 900);
        }
      }
    }

    /* ─── Error Handling ─── */

    _showError(message) {
      if (this.errorWrapper && this.errorMessage) {
        this.errorMessage.textContent = message;
        this.errorWrapper.removeAttribute('hidden');
      }
    }

    _clearError() {
      if (this.errorWrapper) {
        this.errorWrapper.setAttribute('hidden', '');
      }
    }
  }

  customElements.define('product-bundle-form', ProductBundleForm);
}
